import {
  getGoldenRatioDistinctHsl,
  hslToCss,
} from "../../../utils/distinctColors";
import {
  ASSET_PIN_DISPLAY_HEIGHT,
  ASSET_PIN_DISPLAY_WIDTH,
} from "./MapMarkers";

/** First source pin (index 1): navy so it reads clearly on green map tiles. */
const NAVY_PIN_COLORS = {
  colorTop: "hsl(215, 58%, 48%)",
  colorBottom: "hsl(215, 72%, 28%)",
} as const;

const getMarkerColors = (index: number) => {
  if (index === 1) {
    return { ...NAVY_PIN_COLORS };
  }

  const newValue = index + 50;
  const { h, s, l } = getGoldenRatioDistinctHsl(newValue);

  return {
    colorTop: hslToCss({ h, s, l }),
    colorBottom: hslToCss({ h, s, l: l - 10 }),
  };
};

export type SimilarAssetsPinDisplaySize = {
  width: number;
  height: number;
};

/** Deal comps map pins: 25% larger than default asset similar-asset pins (27×42). */
export const COMPS_PIN_DISPLAY_SIZE: SimilarAssetsPinDisplaySize = {
  width: 34,
  height: 53,
};

function similarAssetsPinIcon(
  url: string,
  displaySize: SimilarAssetsPinDisplaySize,
): google.maps.Icon {
  return {
    url,
    scaledSize: new window.google.maps.Size(
      displaySize.width,
      displaySize.height,
    ),
    anchor: new window.google.maps.Point(
      displaySize.width / 2,
      displaySize.height,
    ),
  } as google.maps.Icon;
}

export const buildSVGIconSimilarAssetsSVGIcon = (
  index: number,
  displaySize: SimilarAssetsPinDisplaySize = {
    width: ASSET_PIN_DISPLAY_WIDTH,
    height: ASSET_PIN_DISPLAY_HEIGHT,
  },
) => {
  const { colorTop, colorBottom } = getMarkerColors(index);

  // Unique IDs for gradients/shadows
  const uniqueId = Math.random().toString(36).substring(2, 9);
  const gradientId = `pinGrad-${uniqueId}`;
  const shadowId = `pinShadow-${uniqueId}`;

  const svg = `
      <svg width="24" height="24" viewBox="0 0 24 24"
           xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <defs>
          <linearGradient id="${gradientId}" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="${colorTop}" />
            <stop offset="100%" stop-color="${colorBottom}" />
          </linearGradient>
  
          <filter id="${shadowId}" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="1" stdDeviation="1" flood-opacity="0.25"/>
          </filter>
        </defs>
  
        <path
          d="M12 1.5C7.305 1.5 3.5 5.305 3.5 10c0 5.25 8.5 12.5 8.5 12.5S20.5 15.25 20.5 10c0-4.695-3.805-8.5-8.5-8.5z"
          fill="url(#${gradientId})"
          filter="url(#${shadowId})"/>
  
        <path
          d="M12 2.5c-4.7 0-8.5 3.8-8.5 8.5 0 1.1.2 2.2.6 3.2.2.4.8.5 1.1.1 2-2.7 4.7-4.3 6.8-4.3s4.8 1.6 6.8 4.3c.3.4.9.3 1.1-.1.4-1 .6-2.1.6-3.2 0-4.7-3.8-8.5-8.5-8.5z"
          fill="rgba(255,255,255,0.18)"/>
  
        <circle cx="12" cy="10" r="4.5" fill="white" opacity="0.9"/>
  
        <path
          d="M10.2 8.2h3.6v4.6h-3.6V8.2zm-1.2 3.3h1v1.3h-1v-1.3zm1.8 0h1v1.3h-1v-1.3zm1.8 0h1v1.3h-1v-1.3z"
          fill="${colorBottom}"/>
      </svg>
    `;

  const url = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;

  return similarAssetsPinIcon(url, displaySize);
};

/** Pin styled like {@link buildSVGIconSimilarAssetsSVGIcon} with a count in the center (e.g. stacked comps). */
export const buildSVGIconSimilarAssetsWithDuplicateCount = (
  colorIndex: number,
  duplicateCount: number,
  displaySize: SimilarAssetsPinDisplaySize = {
    width: ASSET_PIN_DISPLAY_WIDTH,
    height: ASSET_PIN_DISPLAY_HEIGHT,
  },
) => {
  const { colorTop, colorBottom } = getMarkerColors(colorIndex);
  const n = Math.max(1, Math.floor(duplicateCount));
  const display = n > 99 ? "99+" : String(n);
  const pinScale = displaySize.width / ASSET_PIN_DISPLAY_WIDTH;
  const baseFontSize =
    display.length > 2 ? 6.5 : display.length > 1 ? 8 : 9;
  const fontSize = baseFontSize * pinScale;

  const uniqueId = Math.random().toString(36).substring(2, 9);
  const gradientId = `pinGrad-${uniqueId}`;
  const shadowId = `pinShadow-${uniqueId}`;

  const svg = `
      <svg width="24" height="24" viewBox="0 0 24 24"
           xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <defs>
          <linearGradient id="${gradientId}" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="${colorTop}" />
            <stop offset="100%" stop-color="${colorBottom}" />
          </linearGradient>

          <filter id="${shadowId}" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="1" stdDeviation="1" flood-opacity="0.25"/>
          </filter>
        </defs>

        <path
          d="M12 1.5C7.305 1.5 3.5 5.305 3.5 10c0 5.25 8.5 12.5 8.5 12.5S20.5 15.25 20.5 10c0-4.695-3.805-8.5-8.5-8.5z"
          fill="url(#${gradientId})"
          filter="url(#${shadowId})"/>

        <path
          d="M12 2.5c-4.7 0-8.5 3.8-8.5 8.5 0 1.1.2 2.2.6 3.2.2.4.8.5 1.1.1 2-2.7 4.7-4.3 6.8-4.3s4.8 1.6 6.8 4.3c.3.4.9.3 1.1-.1.4-1 .6-2.1.6-3.2 0-4.7-3.8-8.5-8.5-8.5z"
          fill="rgba(255,255,255,0.18)"/>

        <circle cx="12" cy="10" r="6" fill="white" opacity="0.95"/>

        <g transform="translate(12,10)">
          <text
            x="0"
            y="0"
            text-anchor="middle"
            dominant-baseline="middle"
            alignment-baseline="middle"
            fill="${colorBottom}"
            font-family="system-ui, -apple-system, Segoe UI, Arial, sans-serif"
            font-size="${fontSize}"
            font-weight="700">${display}</text>
        </g>
      </svg>
    `;

  const url = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;

  return similarAssetsPinIcon(url, displaySize);
};
